import { Table } from '@iveco-ti/iveco-ds'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import { GraphicArea } from '@/common/components/GraphicArea'
import { ProfileAccessSkeleton } from './components/ProfileAccessSkeleton'
import { ProfileAccessTableRow } from './components/ProfileAccessTableRow'
import {
  CurrentUserAccounts,
  useGetCurrentUserAccounts,
} from './hooks/useGetCurrentUserAccounts'

export default function ProfileAccess() {
  const { t } = useTranslation(['profile', 'common'])
  const { currentUserAccounts, isLoadingCurrentUserAccounts } =
    useGetCurrentUserAccounts()

  const getApplicationAccounts = () => {
    if (!currentUserAccounts) return []

    const applicationAccountsMap = new Map()

    currentUserAccounts.ApplicationAccountUsers?.forEach((accountUser) => {
      const application = accountUser.Application
      if (!application) return

      if (!applicationAccountsMap.has(application.id)) {
        applicationAccountsMap.set(application.id, {
          application,
          accountUsers: [],
        })
      }

      applicationAccountsMap.get(application.id).accountUsers.push(accountUser)
    })

    const result = Array.from(applicationAccountsMap.values())

    result.forEach((item) => {
      item.accountUsers.sort(
        (
          a: CurrentUserAccounts['ApplicationAccountUsers'][0],
          b: CurrentUserAccounts['ApplicationAccountUsers'][0],
        ) => {
          if (a.isIngress && !b.isIngress) return -1
          if (!a.isIngress && b.isIngress) return 1
          return 0
        },
      )
    })

    return result
  }

  const applicationAccounts = getApplicationAccounts()

  return (
    <>
      <HeadProvider>
        <Title>{t('access.browser.title')}</Title>
      </HeadProvider>

      <GraphicArea.Root>
        <GraphicArea.Header>
          <h3 className="text-2xl font-semibold text-brand-800">
            {t('access.title')}
          </h3>
        </GraphicArea.Header>

        <GraphicArea.Body>
          <div className="max-h-full overflow-y-auto">
            <div className="space-y-6 mx-3">
              {isLoadingCurrentUserAccounts && <ProfileAccessSkeleton />}

              {!isLoadingCurrentUserAccounts &&
                applicationAccounts.map((item) => {
                  if (!item) return null

                  const { application, accountUsers } = item

                  return (
                    <div key={application.id} className="flex flex-col gap-2">
                      <p className="font-medium text-base text-neutral-800">
                        {application.title}
                      </p>

                      {accountUsers.length > 0 && (
                        <div className="bg-neutral-50 rounded-xl">
                          <Table.Root>
                            <Table.Content>
                              <Table.Head
                                hideCheckbox
                                cols={[
                                  { label: t('access.table.account') },
                                  { label: t('access.table.role') },
                                  {
                                    label: t('access.table.actions'),
                                    end: true,
                                  },
                                ]}
                                onOrderBy={() => {}}
                              />

                              <Table.Body>
                                {accountUsers.map(
                                  (
                                    accountUser: CurrentUserAccounts['ApplicationAccountUsers'][0],
                                  ) => (
                                    <ProfileAccessTableRow
                                      key={accountUser.id}
                                      accountUser={accountUser}
                                      applicationTitle={application.title}
                                      allConnectedAccounts={accountUsers}
                                    />
                                  ),
                                )}
                              </Table.Body>
                            </Table.Content>
                          </Table.Root>
                        </div>
                      )}
                    </div>
                  )
                })}
            </div>
          </div>
        </GraphicArea.Body>
      </GraphicArea.Root>
    </>
  )
}
